import { Page } from '@dixa/lib-shared-habla';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { createContext, useEffect, useMemo } from 'react';

import { bansApi } from 'apis/bans';
import { usersApi } from 'apis/users';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { pubSub } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import {
  contactLoadFailed,
  contactLoading,
  contactNotFound,
  noContactSelected,
} from '../../utils/contactDetails.utils';

import { UserActivityLog } from 'components/activityLog/components/userActivityLog';

import { UserAssignedConversations } from './components/userAssignedConversations/userAssignedConversations';
import { UserBanHistory } from './components/userBanHistory/userBanHistory';
import { UserConversations } from './components/userConversations/userConversations';
import { UserInfo } from './components/userInfo/userInfo';

export const UserBanOnChatContext = createContext<{
  isBanned?: boolean;
  onBanStatusToggle?: () => void;
  isLoadingBanStatus: boolean;
}>({
  isLoadingBanStatus: false,
});

export function UserDetails(): JSX.Element {
  const { id } = useAppPathParams('/contacts/user/:id');

  const { contactActivityLog } = useFeatureFlag(['contact-activity-log']);

  const [getUser, { user, isLoading, isError, isUninitialized }] =
    usersApi.useLazyReadUserQuery({
      selectFromResult: ({ data, ...rest }) => ({
        user: data?.data,
        ...rest,
      }),
    });

  const {
    bans,
    isLoading: isBansHistoryLoading,
    isFetching: isBansHistoryFetching,
    isError: isBansHistoryError,
    refetch: refetchBansHistory,
  } = bansApi.useReadUserBansQuery(
    id ? { userId: id, includeInactive: true } : skipToken,
    {
      selectFromResult: ({ data, ...rest }) => ({
        bans: data?.data.bans ?? [],
        ...rest,
      }),
    },
  );

  const isBannedOnChat = useMemo(
    () =>
      bans.some(
        (ban) =>
          !ban.unbanned_at &&
          (!ban.expire_at || new Date(ban.expire_at) >= new Date()) &&
          ban.channel === 'widget_chat',
      ),
    [bans],
  );

  useEffect(() => {
    const fetchUser = () => {
      if (id) {
        getUser({ id });
      }
    };

    fetchUser();

    const unsubscribe = pubSub.subscribe('userModalUserUpdated', fetchUser);

    return () => unsubscribe();
  }, [id, getUser]);

  if (!id) {
    return noContactSelected;
  }

  if (isLoading || isUninitialized) {
    return contactLoading;
  }

  if (isError) {
    return contactLoadFailed;
  }

  if (!user) {
    return contactNotFound;
  }

  return (
    <Page>
      <Page.Body>
        <Page.Section>
          <UserBanOnChatContext.Provider
            value={{
              isBanned: isBannedOnChat,
              onBanStatusToggle: refetchBansHistory,
              isLoadingBanStatus: isBansHistoryFetching,
            }}
          >
            <UserInfo user={user} />
          </UserBanOnChatContext.Provider>
        </Page.Section>

        {/*
         * Rollout gate: for contacts and bots, the new Activities feed replaces
         * the legacy Conversations table (and, for contacts, Ban history) while
         * `contact-activity-log` is on. Members never get the Activities feed —
         * they keep the Conversations table plus their Assigned conversations.
         * The legacy sections stay as the flag-off experience until the flag is
         * fully rolled out and removed.
         */}
        {contactActivityLog && user.type !== 'member' ? (
          <UserActivityLog userId={user.id} />
        ) : (
          <Page.Section title="Conversations">
            <UserConversations user={user} />
          </Page.Section>
        )}

        {user.type === 'member' && (
          <Page.Section title="Assigned conversations">
            <UserAssignedConversations user={user} />
          </Page.Section>
        )}

        {!contactActivityLog && user.type === 'contact' && (
          <Page.Section title="Ban history">
            <UserBanHistory
              bans={bans}
              isLoading={isBansHistoryLoading || isBansHistoryFetching}
              isError={isBansHistoryError}
            />
          </Page.Section>
        )}
      </Page.Body>
    </Page>
  );
}
